import { CoreScript } from '@safeguardproperties/corescript-react-npm/dist/types';
import { getDataByKey, clearStorage } from './storage';
import { persistor } from './Store';
import { Validation } from '../utils';
import { Platform } from '../constants/platform';

enum PersistKeys {
  CORE_SCRIPT = 'coreScript',
  GALLERY = 'gallery',
  LANGUAGE = 'language',
  ORDER_NUMBER = 'order_number',
  INSPECT_ORDER = 'inspect_order',
  CONTEXT_ID = 'context_id',
  PERMISSION = 'permission',
}
const persistedKey = (key: string) => `persist:${key}`;

// Helper function to validate CORE_SCRIPT data
const validateCoreScript = (data: any, contextId: string): boolean => {
  if (!data) return false;
  const parsedOrder = JSON.parse(data?.contextId || '');
  if (!parsedOrder) return false;

  const script = JSON.parse(data?.coreScript || '') as CoreScript;

  const isValid = Boolean(
    parsedOrder && contextId === parsedOrder && (script?.scriptData?.Name || '')
  );

  return isValid;
};

// Helper function to validate CONTEXT_ID data
const validateContextId = (data: any, contextId: string): boolean => {
  if (!data) return false;
  const savedContextId = JSON.parse(data?.contextId || '');
  const savedParams = JSON.parse(data?.queryObj || '');

  if (!savedContextId || !savedParams) return false;

  const urlSearch = new URLSearchParams(globalThis?.location.search);
  const params: Record<string, string> = {};
  // eslint-disable-next-line unicorn/no-array-for-each
  urlSearch.forEach((value, key) => {
    params[key] = value;
  });

  const isValidParams =
    savedParams.context === params?.context &&
    savedParams.id === params?.id &&
    savedParams.key === params?.key &&
    contextId === savedContextId;

  return isValidParams;
};

// Helper function to validate LANGUAGE data
const validateLanguage = (data: any): boolean => {
  if (!data) return false;

  try {
    const langGeneratedDate = data?.generatedDate || '';
    if (!langGeneratedDate) return false;
    return true;
  } catch {
    return false;
  }
};

// Main function to validate all keys
const isDataPersisted = async (contextId: string): Promise<boolean> => {
  try {
    //if it's audit then check next steps
    if (!Platform.isAudit()) return false;

    //** just putting in dev branch to test f=1 for force reload */
    const url = new URL(globalThis.location.href);
    const params = new URLSearchParams(url.search);
    const queryObject: Record<string, string> = {};
    // eslint-disable-next-line unicorn/no-array-for-each
    params.forEach((value, key) => {
      queryObject[key] = value;
    });
    const isLoadAgain = Object.prototype.hasOwnProperty.call(queryObject, 'f');
    //** just putting in dev branch to test f=1 for force reload */
    if (!Validation.isPersistEnabled()) {
      await PersistHelper.clearAllData();
      return false;
    }

    const coreScriptKey = persistedKey(PersistKeys.CORE_SCRIPT);
    const contextIdKey = persistedKey(PersistKeys.CONTEXT_ID);
    const languageKey = persistedKey(PersistKeys.LANGUAGE);

    const [coreScriptData, contextIdData, languageData] = await Promise.all([
      getDataByKey(coreScriptKey),
      getDataByKey(contextIdKey),
      getDataByKey(languageKey),
    ]);

    const isCoreScriptValid = validateCoreScript(coreScriptData, contextId);
    const isContextIdValid = validateContextId(contextIdData, contextId);
    const isLanguageValid = validateLanguage(languageData);

    const isValid =
      isCoreScriptValid &&
      isContextIdValid &&
      isLanguageValid &&
      isLoadAgain === false;

    if (!isValid) {
      await persistor.purge();
    }

    return isValid;
  } catch (error: any) {
    console.log('Error in persist validation', error);
    await persistor.purge();
    return false;
  }
};

const clearAllData = async () => {
  await persistor.purge();
  localStorage.clear();
  clearStorage();
};
export const PersistHelper = {
  PersistKeys,
  isDataPersisted,
  clearAllData,
  persistedKey,
};
